import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  FileQuestion, 
  Wand2, 
  Upload, 
  X, 
  Users, 
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  FileText,
  Image,
  File,
  Mail,
  Info
} from 'lucide-react';

export default function RFIForm({ 
  projectId, 
  rfiId = null, 
  open, 
  onOpenChange, 
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [professionalTeam, setProfessionalTeam] = useState([]);
  const [internalTeam, setInternalTeam] = useState([]);
  const [expandedDisciplines, setExpandedDisciplines] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [siteInstructions, setSiteInstructions] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [variationOrders, setVariationOrders] = useState([]);
  const fileInputRef = useRef(null);
  const [showStartingPrompt, setShowStartingPrompt] = useState(false);
  const [rfiStartingNumber, setRfiStartingNumber] = useState('1');
  
  const [formData, setFormData] = useState({
    contract_name: '',
    discipline: '',
    priority: 'Medium',
    title: '',
    query: '',
    query_formatted: '',
    required_response_date: '',
    distribution_type: 'external',
    recipients: [],
    linked_activity: '',
    affects_critical_path: false,
    variation_delay_risk: 'none',
    related_si: '',
    related_drawing: '',
    related_vo: ''
  });

  useEffect(() => {
    if (open) {
      loadReferenceData();
      loadTeams();
      if (rfiId) {
        loadRFI();
      } else {
        resetForm();
        checkStartingNumber();
      }
    }
  }, [open, rfiId]);

  const checkStartingNumber = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/starting-numbers`);
      const data = res.data;
      if (!data.rfi_configured && data.rfi_count === 0) {
        setShowStartingPrompt(true);
      }
    } catch {}
  };

  const handleConfirmRfiStarting = async () => {
    const num = parseInt(rfiStartingNumber);
    if (isNaN(num) || num < 1) {
      toast.error('Please enter a valid starting number');
      return;
    }
    try {
      await api.post(`/projects/${projectId}/starting-numbers`, { rfi_starting_number: num });
      setShowStartingPrompt(false);
      toast.success(`RFI numbering will start from RFI-${String(num).padStart(4, '0')}`);
    } catch {
      toast.error('Failed to save starting number');
    }
  };

  const resetForm = () => {
    setFormData({
      contract_name: '',
      discipline: '',
      priority: 'Medium',
      title: '',
      query: '',
      query_formatted: '',
      required_response_date: '',
      distribution_type: 'external',
      recipients: [],
      linked_activity: '',
      affects_critical_path: false,
      variation_delay_risk: 'none',
      related_si: '',
      related_drawing: '',
      related_vo: ''
    });
    setPendingFiles([]);
  };

  const loadReferenceData = async () => {
    try {
      const [discRes, prioRes] = await Promise.all([
        api.rfi.getDisciplines(),
        api.rfi.getPriorities()
      ]);
      setDisciplines(discRes.data);
      setPriorities(prioRes.data);
      
      // Load project-specific data (SIs, Drawings, VOs)
      if (projectId) {
        try {
          const [siRes, drawRes, voRes] = await Promise.all([
            api.get(`/projects/${projectId}/site-instructions`),
            api.get(`/projects/${projectId}/drawings`),
            api.get(`/projects/${projectId}/variation-orders`)
          ]);
          
          const sis = Array.isArray(siRes.data) ? siRes.data : (siRes.data?.site_instructions || []);
          setSiteInstructions(sis);
          
          const draws = Array.isArray(drawRes.data) ? drawRes.data : (drawRes.data?.drawings || []);
          setDrawings(draws);
          
          const vos = Array.isArray(voRes.data) ? voRes.data : (voRes.data?.variation_orders || []);
          setVariationOrders(vos);
        } catch (err) {
          console.log('Could not load project reference data:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load reference data');
    }
  };

  const loadTeams = async () => {
    try {
      const [profRes, intRes] = await Promise.all([
        api.professionalTeam.getAll(projectId),
        api.internalTeam.getAll(projectId)
      ]);
      setProfessionalTeam(profRes.data || []);
      setInternalTeam(intRes.data || []);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const loadRFI = async () => {
    setLoading(true);
    try {
      const response = await api.rfi.getById(projectId, rfiId);
      const rfi = response.data;
      setFormData({
        contract_name: rfi.contract_name || '',
        discipline: rfi.discipline || '',
        priority: rfi.priority || 'Medium',
        title: rfi.title || '',
        query: rfi.query || '',
        query_formatted: rfi.query_formatted || '',
        required_response_date: rfi.required_response_date?.split('T')[0] || '',
        distribution_type: rfi.distribution_type || 'external',
        recipients: rfi.recipients || [],
        linked_activity: rfi.linked_activity || '',
        affects_critical_path: rfi.affects_critical_path || false,
        variation_delay_risk: rfi.variation_delay_risk || 'none',
        related_si: rfi.related_si || '',
        related_drawing: rfi.related_drawing || '',
        related_vo: rfi.related_vo || ''
      });
    } catch (error) {
      toast.error('Failed to load RFI');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientToggle = (member, isInternal) => {
    // Build full name for internal team (name + surname) or use contact_name for professional
    const displayName = isInternal 
      ? `${member.name || ''} ${member.surname || ''}`.trim() || 'Unknown'
      : member.contact_name || 'Unknown';
    
    const recipient = {
      id: member.id,
      name: displayName,
      email: isInternal ? member.email : member.contact_email,
      company: isInternal ? 'Internal' : member.company_name,
      discipline: isInternal ? member.department : member.discipline,
      is_internal: isInternal,
      is_cc: false
    };
    
    setFormData(prev => {
      const exists = prev.recipients.find(r => r.id === member.id);
      if (exists) {
        return {
          ...prev,
          recipients: prev.recipients.filter(r => r.id !== member.id)
        };
      } else {
        return {
          ...prev,
          recipients: [...prev.recipients, recipient]
        };
      }
    });
  };

  const toggleRecipientCC = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === recipientId ? { ...r, is_cc: !r.is_cc } : r
      )
    }));
  };

  const handleSelectAllDiscipline = (discipline, members, isInternal) => {
    const disciplineMembers = members.filter(m => 
      isInternal ? m.department === discipline : m.discipline === discipline
    );
    
    setFormData(prev => {
      const currentIds = prev.recipients.map(r => r.id);
      const disciplineMemberIds = disciplineMembers.map(m => m.id);
      const allSelected = disciplineMemberIds.every(id => currentIds.includes(id));
      
      if (allSelected) {
        // Remove all from this discipline
        return {
          ...prev,
          recipients: prev.recipients.filter(r => !disciplineMemberIds.includes(r.id))
        };
      } else {
        // Add all from this discipline
        const newRecipients = disciplineMembers
          .filter(m => !currentIds.includes(m.id))
          .map(m => ({
            id: m.id,
            name: isInternal ? m.name : m.contact_name,
            email: isInternal ? m.email : m.contact_email,
            company: isInternal ? 'Internal' : m.company_name,
            discipline: isInternal ? m.department : m.discipline,
            is_internal: isInternal,
            is_cc: false
          }));
        return {
          ...prev,
          recipients: [...prev.recipients, ...newRecipients]
        };
      }
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files]);
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    if (['pdf', 'doc', 'docx'].includes(ext)) return <FileText className="h-4 w-4 text-red-500" />;
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return <Image className="h-4 w-4 text-blue-500" />;
    if (['dwg', 'dxf'].includes(ext)) return <File className="h-4 w-4 text-purple-500" />;
    if (['msg', 'eml'].includes(ext)) return <Mail className="h-4 w-4 text-orange-500" />;
    return <File className="h-4 w-4 text-slate-500" />;
  };

  const handleRewriteQuery = async () => {
    if (!formData.query.trim()) {
      toast.error('Please enter a query first');
      return;
    }
    
    // If creating new RFI, validate required fields first
    if (!rfiId) {
      if (!formData.discipline) {
        toast.error('Please select a discipline before rewriting');
        return;
      }
      if (!formData.title) {
        toast.error('Please enter a title before rewriting');
        return;
      }
    }
    
    setRewriting(true);
    try {
      // Create RFI first if new, then rewrite
      let currentRfiId = rfiId;
      if (!currentRfiId) {
        const createRes = await api.rfi.create(projectId, formData);
        currentRfiId = createRes.data.id;
        // Update parent state with new RFI ID
        toast.success('RFI draft created');
      }
      
      const response = await api.rfi.rewriteQuery(projectId, currentRfiId);
      setFormData(prev => ({ ...prev, query_formatted: response.data.formatted_query }));
      toast.success('Query rewritten in professional format');
    } catch (error) {
      console.error('Rewrite failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to rewrite query';
      toast.error(errorMessage);
    } finally {
      setRewriting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.discipline || !formData.title || !formData.query) {
      toast.error('Please fill in required fields: Discipline, Title, and Query');
      return;
    }
    
    setSaving(true);
    try {
      let currentRfiId = rfiId;
      
      if (rfiId) {
        await api.rfi.update(projectId, rfiId, formData);
      } else {
        const response = await api.rfi.create(projectId, formData);
        currentRfiId = response.data.id;
      }
      
      // Upload any pending files
      if (pendingFiles.length > 0 && currentRfiId) {
        for (const file of pendingFiles) {
          const formDataFile = new FormData();
          formDataFile.append('file', file);
          try {
            await api.rfi.uploadAttachment(projectId, currentRfiId, formDataFile);
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }
      
      toast.success(rfiId ? 'RFI updated' : 'RFI created successfully');
      onSuccess?.(currentRfiId);
      onOpenChange(false);
    } catch (error) {
      console.error('RFI save failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save RFI';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Group professional team by discipline
  const groupedProfessionalTeam = professionalTeam.reduce((acc, member) => {
    const disc = member.discipline || 'Other';
    if (!acc[disc]) acc[disc] = [];
    acc[disc].push(member);
    return acc;
  }, {});

  // Group internal team by department
  const groupedInternalTeam = internalTeam.reduce((acc, member) => {
    const dept = member.department || 'General';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(member);
    return acc;
  }, {});

  const showExternalTeam = formData.distribution_type === 'external' || formData.distribution_type === 'both';
  const showInternalTeam = formData.distribution_type === 'internal' || formData.distribution_type === 'both';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {showStartingPrompt && !rfiId ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Info className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Set Starting RFI Number</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              This is the first RFI for this project. If you're joining mid-project, enter the number your RFIs should start from.
            </p>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">RFI-</span>
              <Input
                type="number"
                min="1"
                value={rfiStartingNumber}
                onChange={(e) => setRfiStartingNumber(e.target.value)}
                className="w-32"
                placeholder="1"
                data-testid="rfi-starting-number-input"
                autoFocus
              />
              <span className="text-xs text-slate-500">First RFI will be RFI-{String(parseInt(rfiStartingNumber) || 1).padStart(4, '0')}</span>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleConfirmRfiStarting} className="flex-1 bg-slate-900 text-white hover:bg-slate-800" data-testid="rfi-starting-confirm">
                Confirm
              </Button>
              <Button variant="outline" onClick={() => setShowStartingPrompt(false)} className="flex-1" data-testid="rfi-starting-skip">
                Start from 1
              </Button>
            </div>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
            <FileQuestion className="h-5 w-5" />
            {rfiId ? 'Edit RFI' : 'Create New RFI'}
          </DialogTitle>
          <DialogDescription>
            Fill in the RFI details below. Use AI to rewrite your query in professional format.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Header Information */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Discipline *
                </Label>
                <Select 
                  value={formData.discipline} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, discipline: v }))}
                >
                  <SelectTrigger data-testid="discipline-select">
                    <SelectValue placeholder="Select discipline" />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplines.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Priority
                </Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(v) => {
                    // Prevent downgrading below High when critical path is affected
                    if (formData.affects_critical_path) {
                      const lowPriorities = ['low', 'medium'];
                      if (lowPriorities.includes(v.toLowerCase())) {
                        return; // Block downgrade
                      }
                    }
                    setFormData(prev => ({ ...prev, priority: v }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Response Due Date
                </Label>
                <Input
                  type="date"
                  value={formData.required_response_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, required_response_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Title *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief description of the information required"
                data-testid="rfi-title-input"
              />
            </div>

            {/* Query */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Query *
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRewriteQuery}
                  disabled={rewriting || !formData.query.trim()}
                  className="text-xs"
                >
                  {rewriting ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Wand2 className="h-3 w-3 mr-1" />
                  )}
                  Rewrite in Professional Format
                </Button>
              </div>
              <Textarea
                value={formData.query}
                onChange={(e) => setFormData(prev => ({ ...prev, query: e.target.value }))}
                placeholder="Describe the information you require in detail..."
                rows={5}
                data-testid="rfi-query-input"
              />
              {formData.query_formatted && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-sm">
                  <p className="text-xs font-bold uppercase text-green-700 mb-1">AI Formatted Version</p>
                  <p className="text-sm text-green-800 whitespace-pre-wrap">{formData.query_formatted}</p>
                </div>
              )}
            </div>

            {/* Linked Activity */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Linked Activity (Optional)
              </Label>
              <Input
                value={formData.linked_activity}
                onChange={(e) => setFormData(prev => ({ ...prev, linked_activity: e.target.value }))}
                placeholder="e.g., Foundation Works, Structural Steel Erection"
              />
            </div>

            {/* Related References - SI, Drawing, VO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Related Site Instruction */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Related SI (Optional)
                </Label>
                <select
                  value={formData.related_si}
                  onChange={(e) => setFormData(prev => ({ ...prev, related_si: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select Site Instruction</option>
                  {siteInstructions.map(si => (
                    <option key={si.id} value={si.id}>
                      {si.si_number || si.reference_number} - {si.title?.substring(0, 30) || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Related Drawing */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Related Drawing (Optional)
                </Label>
                <select
                  value={formData.related_drawing}
                  onChange={(e) => setFormData(prev => ({ ...prev, related_drawing: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select Drawing</option>
                  {drawings.map(drw => (
                    <option key={drw.id} value={drw.id}>
                      {drw.drawing_number || drw.number} - {drw.title?.substring(0, 30) || drw.name?.substring(0, 30) || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Related VO */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Related VO (Optional)
                </Label>
                <select
                  value={formData.related_vo}
                  onChange={(e) => setFormData(prev => ({ ...prev, related_vo: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select Variation Order</option>
                  {variationOrders.map(vo => (
                    <option key={vo.id} value={vo.id}>
                      {vo.vo_number} - {vo.title?.substring(0, 30) || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Critical Path Impact */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="critical-path"
                checked={formData.affects_critical_path}
                onCheckedChange={(checked) => {
                  const updates = { affects_critical_path: checked };
                  // Auto-escalate priority to at least High when critical path is affected
                  if (checked) {
                    const currentPrio = (formData.priority || '').toLowerCase();
                    if (currentPrio === 'low' || currentPrio === 'medium') {
                      updates.priority = 'High';
                    }
                  }
                  setFormData(prev => ({ ...prev, ...updates }));
                }}
              />
              <label
                htmlFor="critical-path"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                This RFI affects the Critical Path
              </label>
            </div>

            {/* Variation / Delay Risk */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Variation / Delay Risk
              </Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: 'none', label: 'None' },
                  { id: 'time', label: 'Potential Time Impact' },
                  { id: 'cost', label: 'Potential Cost Impact' },
                  { id: 'time_and_cost', label: 'Time & Cost Impact' },
                  { id: 'critical_path', label: 'Critical Path Affected' }
                ].map(option => (
                  <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="variation_delay_risk"
                      value={option.id}
                      checked={formData.variation_delay_risk === option.id}
                      onChange={(e) => setFormData(prev => ({ ...prev, variation_delay_risk: e.target.value }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Distribution */}
            <div className="border border-slate-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-4 w-4 text-slate-600" />
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Distribution
                </Label>
              </div>
              
              <div className="flex gap-4 mb-4">
                {['internal', 'external', 'both'].map(type => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="distribution_type"
                      value={type}
                      checked={formData.distribution_type === type}
                      onChange={(e) => setFormData(prev => ({ ...prev, distribution_type: e.target.value }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm capitalize">{type === 'both' ? 'Internal + External' : type + ' Only'}</span>
                  </label>
                ))}
              </div>

              {/* Selected Recipients */}
              {formData.recipients.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">
                    Selected Recipients ({formData.recipients.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.recipients.map(r => (
                      <Badge 
                        key={r.id} 
                        className={`${r.is_cc ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-700'} cursor-pointer`}
                        onClick={() => toggleRecipientCC(r.id)}
                      >
                        {r.name || r.email || 'Unknown'} {r.is_cc && '(CC)'}
                        <X 
                          className="h-3 w-3 ml-1 hover:text-red-500" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecipientToggle({ id: r.id }, r.is_internal);
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* External Team Selection */}
              {showExternalTeam && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <p className="text-xs font-bold uppercase text-slate-500">Professional Team</p>
                  </div>
                  <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                    {Object.entries(groupedProfessionalTeam).map(([discipline, members]) => (
                      <div key={discipline}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100"
                          onClick={() => setExpandedDisciplines(prev => ({ ...prev, [discipline]: !prev[discipline] }))}
                        >
                          <span className="text-sm font-medium">{discipline}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{members.length}</Badge>
                            {expandedDisciplines[discipline] ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </div>
                        </button>
                        {expandedDisciplines[discipline] && (
                          <div className="px-3 py-2 space-y-1">
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => handleSelectAllDiscipline(discipline, professionalTeam, false)}
                            >
                              Select All {discipline}
                            </button>
                            {members.map(member => (
                              <label key={member.id} className="flex items-center gap-2 cursor-pointer py-1">
                                <Checkbox
                                  checked={formData.recipients.some(r => r.id === member.id)}
                                  onCheckedChange={() => handleRecipientToggle(member, false)}
                                />
                                <span className="text-sm">
                                  {member.contact_name} - {member.company_name}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {Object.keys(groupedProfessionalTeam).length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        No professional team members added
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Internal Team Selection */}
              {showInternalTeam && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-slate-500" />
                    <p className="text-xs font-bold uppercase text-slate-500">Internal Team</p>
                  </div>
                  <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                    {Object.entries(groupedInternalTeam).map(([dept, members]) => (
                      <div key={dept}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100"
                          onClick={() => setExpandedDisciplines(prev => ({ ...prev, [`int_${dept}`]: !prev[`int_${dept}`] }))}
                        >
                          <span className="text-sm font-medium">{dept}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{members.length}</Badge>
                            {expandedDisciplines[`int_${dept}`] ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </div>
                        </button>
                        {expandedDisciplines[`int_${dept}`] && (
                          <div className="px-3 py-2 space-y-1">
                            {members.map(member => (
                              <label key={member.id} className="flex items-center gap-2 cursor-pointer py-1">
                                <Checkbox
                                  checked={formData.recipients.some(r => r.id === member.id)}
                                  onCheckedChange={() => handleRecipientToggle(member, true)}
                                />
                                <span className="text-sm">
                                  {member.name} {member.surname} - {member.position}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {Object.keys(groupedInternalTeam).length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        No internal team members added
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Attachments
              </Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                className="hidden"
                accept=".pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.msg,.eml"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-400'
                }`}
              >
                <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
                <p className={`text-sm font-medium ${isDragging ? 'text-blue-600' : 'text-slate-600'}`}>
                  {isDragging ? 'Drop files here' : 'Drag & drop files here or click to browse'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  PDF, DWG, Images, Documents, Emails (.msg, .eml)
                </p>
              </div>
              {pendingFiles.length > 0 && (
                <div className="mt-3 space-y-1">
                  {pendingFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-sm p-2 border border-slate-100">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file)}
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-slate-400">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button type="button" onClick={() => removePendingFile(idx)} className="p-1 hover:bg-slate-200 rounded">
                        <X className="h-4 w-4 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="save-rfi-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  rfiId ? 'Update RFI' : 'Create RFI'
                )}
              </Button>
            </div>
          </form>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
